import { useState, useEffect, useMemo } from 'react';
import {
  Unarchive,
  Archive,
  CommentOutlined,
  InfoOutlined,
  MailOutline,
  EventAvailableOutlined,
  EventNoteOutlined,
} from '@mui/icons-material';

import { Badge, formControlClasses } from '@mui/material';
import { useGetItemStatusesQuery } from 'store/api';
import { ItemInfoTab } from './tabs/ItemInfoTab';
import { CoordinationTab } from './tabs/CoordinationTab';
import {
  ItemContext,
  ItemContextContent,
  ItemProvider,
  useItemContext,
} from 'contexts/item.context';
import {
  AlertDialogProps,
  ControlledTabDrawer,
  DrawerTabProps,
  TabDrawerProps,
  selectUser,
} from '@centerpiece/components';
import { IconButton, Tooltip, useMediaQuery, useTheme } from '@mui/material';

import {
  Event,
  ItemStatus,
  ITEMSTATUSES,
  Coordination,
  Task,
  Item,
} from 'store/api/models';
import { getItemStatus, getStatusColorFromDate } from 'utils/status.utils';

import { useLocation } from 'react-router-dom';
import { useTutorialContext } from 'tutorials/tutorial.context';
import { CombinedEventTab } from './tabs/CombinedEventTab';
import { EventAvailabilityTab } from './tabs/EventAvailabilityTab';
import { CommentsTab } from './tabs/CommentsTab';

export interface ItemTabDrawerProps
  extends Omit<TabDrawerProps, 'title' | 'setTabs' | 'status' | 'tabs'> {
  id: number;
  project_id: number;
  transparentBackdrop?: boolean;
  createProject?: Function;
  afterSave?: Function;
  template?: boolean;
  disabled?: boolean;
  breadcrumbs?: { label: string; onClick?: Function }[];
  exampleItem?: { title: string; status: any };
  alert?: (alert: AlertDialogProps) => void;
}

export const ItemTabDrawer = (props: ItemTabDrawerProps) => {
  return (
    <ItemProvider {...props}>
      <ItemContext.Consumer>
        {contextProps => <ItemComponent {...props} {...contextProps} />}
      </ItemContext.Consumer>
    </ItemProvider>
  );
};

const ItemComponent = (props: ItemContextContent & ItemTabDrawerProps) => {
  const {
    id,
    item,
    itemLoading,
    patchItem,
    coordination,
    event,
    task,
    refetchItem,
    numChats,
    numMsgs,
    numAvails,
    submit,
    messages,
    item_type,
    status,
    setStatus,
    title,
    setTitle,
    activeTab,
    setActiveTab,
    project_id,
    template = false,
    afterSave = () => {},
    disabled = false,
    breadcrumbs,
    comments,
    commentsLoading,
    relationships,
  } = props;
  const { data: statuses = [] } = useGetItemStatusesQuery({});
  const initialTabs = useMemo(
    () => ({
      info: {
        key: 'info',
        label: 'Information',
        disabled: true,
        sequence: 1,
        visible: true,
        icon: <InfoOutlined sx={{ mt: 1 }} />,
        contents: <ItemInfoTab />,
      },
      message: {
        key: 'message',
        label: 'Messaging',
        sequence: 2,
        visible: item?.form?.tabs?.['message']?.visible,
        icon: (
          <Badge sx={{ mt: 1 }} badgeContent={numMsgs} color="secondary">
            <MailOutline />
          </Badge>
        ),
        contents: (
          <CoordinationTab
            exampleCoordination={coordination}
            id={coordination?.id}
            // project={project}
            afterSave={afterSave}
          />
        ),
      },
      // schedule: {
      //   key: 'schedule',
      //   sequence: 3,
      //   visible: isEvent || item?.form?.tabs?.['schedule']?.visible,
      //   label: 'Availability',
      //   icon: (
      //     <Badge sx={{ mt: 1 }} badgeContent={numAvails} color="secondary">
      //       <EventAvailableOutlined />
      //     </Badge>
      //   ),
      //   contents: (
      //     <ScheduleTab
      //       id={event?.id}
      //       // project={project}
      //       afterSave={afterSave}
      //     />
      //   ),
      // },
      event: {
        key: 'event',
        sequence: 4,
        visible: item?.form?.tabs?.['event']?.visible,
        label: 'Event',
        icon: (
          <Badge sx={{ mt: 1 }} badgeContent={numAvails} color="secondary">
            <EventNoteOutlined />
          </Badge>
        ),
        contents: (
          <EventAvailabilityTab />
          // <EventActualTab
          // // id={event?.id}
          // // project={project}
          // // afterSave={afterSave}
          // />
        ),
      },
      comment: {
        key: 'comment',
        label: 'Comment',
        sequence: 5,
        disabled: true,
        visible: true,
        icon: (
          <Badge sx={{ mt: 1 }} badgeContent={numChats} color="secondary">
            <CommentOutlined />
          </Badge>
        ),
        padding: true,
        contents: (
          <CommentsTab
            comments={comments}
            commentsLoading={commentsLoading}
            recipients={relationships}
            afterSave={afterSave}
            foreignKey={{ item: id }}
          />
        ),
      },
    }),
    [
      coordination,
      item,
      numChats,
      numAvails,
      numMsgs,
      comments,
      commentsLoading,
      relationships,
    ]
  );

  const [tabs, setTabsActual] = useState<{ [key: string]: DrawerTabProps }>(
    initialTabs || {}
  );

  useEffect(() => setTabsActual(initialTabs), [initialTabs]);

  const setTabs = async (tabs: { [key: string]: DrawerTabProps }) => {
    let itemTabs: { [key: string]: { visible: boolean } } = {};
    Object.keys(tabs).forEach(tab => {
      itemTabs[tab] = { visible: tabs[tab]?.visible };
    });
    await patchItem({
      id: id,
      form: { tabs: itemTabs },
    });
    setTabsActual(tabs);
  };
  const theme = useTheme();
  const { state, setState } = useTutorialContext();

  useEffect(() => {
    if (props.open && state.tourActive) {
      setActiveTab('message');
      setTimeout(() => {
        setState(currState => ({
          ...currState,
          run: true,
        }));
      }, 750);
    }
    // eslint-disable-next-line
  }, [props.open]);
  return (
    <ControlledTabDrawer
      {...props}
      tabSelection={true}
      width={{ xs: '100%' }}
      maxWidth={theme.breakpoints.values.md}
      isLoading={itemLoading}
      contentLoading={itemLoading}
      breadcrumbs={breadcrumbs}
      status={getItemStatus({
        status: status,
        statuses: statuses,
        disabled: disabled || template,
        color:
          event?.status === ITEMSTATUSES.COMPLETE
            ? 'complete'
            : getStatusColorFromDate(event?.due_date)[0],
        onClick: (sts: any) => {
          setStatus(sts);
          submit({ newStatus: sts.id });

          afterSave();
        },
      })}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      transparentBackdrop={!!props.transparentBackdrop}
      title={title}
      setTitle={setTitle}
      save={() => submit({})}
      icons={[
        <Tooltip title={item?.archived ? 'Unarchive' : 'Archive'}>
          <IconButton
            color="primary"
            onClick={async () => {
              await patchItem({
                id: id,
                archived: !item?.archived,
              } as Item);
              afterSave();
            }}
          >
            {item?.archived ? <Unarchive /> : <Archive />}
          </IconButton>
        </Tooltip>,
      ]}
      tabs={tabs}
      setTabs={setTabs}
    />
  );
};
